import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useToastList, type Toast } from "./ToastContext";

/**
 * Visual layer for the toast system.
 *
 * `<ToastViewport>` renders the live toast stack in a fixed corner,
 * portaled to `document.body` so it floats above every surface —
 * including the Coach Review composer drawer and focus-mode overlay
 * (which themselves portal at `z-[140]`-ish; the viewport sits at
 * `z-[200]`).
 *
 * The approved prototype uses one compact navy announcement at the
 * bottom centre. Tone is retained as data and through live-region roles,
 * while the visible treatment stays calm and consistent.
 *
 * Accessibility: each toast card owns its live-region role. Success /
 * info use `role="status"`; errors use `role="alert"` so failed saves
 * are announced assertively.
 */

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: () => void;
}) {
  return (
    <div
      // Errors assert; success / info post politely.
      role={toast.tone === "error" ? "alert" : "status"}
      data-testid="toast"
      data-toast-tone={toast.tone}
      className="pointer-events-auto w-max max-w-[calc(100vw-2rem)] rounded-[18px] bg-navy px-[22px] py-4 text-paper shadow-pop"
    >
      <div className="flex min-w-0 max-w-[456px] items-start gap-3">
        <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-5 text-paper">
          {toast.message}
        </p>
        {toast.description ? (
          <p className="mt-1 break-words text-xs leading-5 text-paper/75">
            {toast.description}
          </p>
        ) : null}
        </div>
        <button
          type="button"
          aria-label="Dismiss notification"
          data-testid="toast-dismiss"
          onClick={onDismiss}
          className="-mr-1 -mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-paper/80 transition-colors hover:bg-paper/10 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
        >
          <X aria-hidden="true" size={17} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

export function ToastViewport() {
  const { toasts, dismiss } = useToastList();
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-4 bottom-[25px] z-[200] flex flex-col items-center gap-2"
      data-testid="toast-viewport"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}
